// src/components/groups/FederationMembershipStatement.jsx
//
// A federated group's peer-membership statement — "A member of the {group} Federation" — with
// the federation's name given its own line, visual weight, and a click-through. Split out of
// GroupHomePanel's PART OF block (Federation 4D → membership-line-split, 13 Sep): containment
// (a sub-group sits INSIDE its parent — PART OF is right for that) and peer membership (a
// federated group belongs to its federation as an equal, not a possession) are different
// relationships and don't share a block. Two call sites, one body of logic:
//   - Desktop: GroupHomeColumn's pinned ModeColumn footer, above the logo/tagline.
//   - Mobile: MobileGroupFrontPage's Home-tab case, below GroupHomePanel's scrollable body.
// Neither GroupHomePanel nor GroupHomeColumn's body ever renders this — only these two pinned
// slots do, so there is no double-render to manage.
//
// Translation: ONE key, ONE {group} placeholder — pt-PT puts the noun before the name ("Membro
// da Federação {group}"), English puts it in the middle ("A member of the {group} Federation");
// a fixed three-slot template can't hold both word orders, so this can't be three keys or a
// concatenation. A Unicode Private Use Area sentinel (never occurs in real translated text) is
// passed as the `group` variable — using t()'s documented substitution path, since
// resolveTranslation resolves both {group} and {{group}} the same way — then the RESULT is
// split on that same known sentinel to find the name's position, without caring which brace
// form the template used and without relying on any undocumented resolveTranslation behaviour
// (an earlier draft omitted the variable and relied on an unresolved placeholder surviving
// literally — an implementation detail, not a contract — and the Architect overrode it for
// exactly that reason).

import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';

const FederationMembershipStatement = ({ federationGroup, onGroupSelect }) => {
  const theme = useTheme();
  const { t } = useTranslation('groups');
  const mauve = theme?.primary || tokens.colors.contextGroups;

  if (!federationGroup) return null;

  const GROUP_SENTINEL = ''; // Unicode Private Use Area codepoint — cannot occur in real translated text
  const [before, after] = t('federation_membership_line', { group: GROUP_SENTINEL })
    .split(GROUP_SENTINEL)
    .map(part => part.trim());

  return (
    <div style={{ textAlign: 'center', padding: '14px 10px 2px' }}>
      {before && (
        <div style={{ fontSize: '11.5px', color: tokens.colors.textSecondary, lineHeight: '1.4' }}>
          {before}
        </div>
      )}
      <div
        onClick={() => onGroupSelect?.(federationGroup)}
        style={{
          fontSize: '14px', fontWeight: 'bold', color: mauve, cursor: 'pointer',
          lineHeight: '1.4', margin: '2px 0'
        }}
      >
        {federationGroup.name}
      </div>
      {after && (
        <div style={{ fontSize: '11.5px', color: tokens.colors.textSecondary, lineHeight: '1.4' }}>
          {after}
        </div>
      )}
    </div>
  );
};

export default FederationMembershipStatement;
